import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { FileText, Upload, Key, X, Search, Loader, Eye, ScanLine, Paperclip } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from './LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';

// Konfiguracja worker dla pdf.js - użyj workera z wersji używanej przez react-pdf (5.4.296)
// Worker musi być z tej samej wersji co API
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const AddInvoiceModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [sourceType, setSourceType] = useState(null); // 'ksef' lub 'file'
  // eslint-disable-next-line no-unused-vars
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [additionalAttachments, setAdditionalAttachments] = useState([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [employees, setEmployees] = useState([]);
  
  // KSeF - automatyczne pobieranie faktur
  const [searchingKsef, setSearchingKsef] = useState(false);
  const [ksefInvoices, setKsefInvoices] = useState([]);
  const [ksefDateFrom, setKsefDateFrom] = useState('');
  const [ksefDateTo, setKsefDateTo] = useState('');
  const [ksefSubjectType, setKsefSubjectType] = useState('Subject2');
  const [importedKsefReferences, setImportedKsefReferences] = useState(new Set());
  const [selectedKsefInvoice, setSelectedKsefInvoice] = useState(null);
  const [selectedKsefInvoices, setSelectedKsefInvoices] = useState(new Set()); // Wielokrotny wybór do importu
  const [manualAmounts, setManualAmounts] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingAttachments, setIsDraggingAttachments] = useState(false);
  const [scanningField, setScanningField] = useState(null); // 'supplierName' | 'supplierNip' | 'number' | null
  const [contractorResults, setContractorResults] = useState([]);
  const [contractorSearchLoading, setContractorSearchLoading] = useState(false);
  const [showContractorDropdown, setShowContractorDropdown] = useState(false);
  const contractorSearchRef = useRef(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfError, setPdfError] = useState(null);
  const [useIframeFallback, setUseIframeFallback] = useState(false);
  const pdfContainerRef = useRef(null);
  const pdfIframeRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [pageAspectRatio, setPageAspectRatio] = useState(1.414); // A4 default (height/width)
  const [pdfZoom, setPdfZoom] = useState(1); // 1 = dopasowanie do okna
  
  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm({
    defaultValues: {
      currency: 'PLN',
      status: 'NEW'
    }
  });

  const supplierNameValue = watch('supplierName') || '';

  // Wyszukiwanie kontrahentów (debounced)
  useEffect(() => {
    if (!supplierNameValue || supplierNameValue.length < 2) {
      setContractorResults([]);
      setShowContractorDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      setContractorSearchLoading(true);
      try {
        const res = await api.get('/contractors/search', { params: { q: supplierNameValue } });
        setContractorResults(res.data || []);
        setShowContractorDropdown((res.data || []).length > 0);
      } catch (err) {
        setContractorResults([]);
      } finally {
        setContractorSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [supplierNameValue]);

  // Zamknij dropdown przy kliknięciu poza
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contractorSearchRef.current && !contractorSearchRef.current.contains(e.target)) {
        setShowContractorDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Obserwuj zmiany w kwocie netto i stawce VAT
  const amountNet = watch('amountNet');
  const vatRate = watch('vatRate');

  useEffect(() => {
    if (isOpen) {
      // Ustaw domyślne daty dla KSeF (ostatnie 7 dni)
      const today = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(today.getDate() - 7);
      
      const dateToStr = today.toISOString().split('T')[0] + 'T23:59:59';
      const dateFromStr = sevenDaysAgo.toISOString().split('T')[0] + 'T00:00:00';
      
      setKsefDateTo(dateToStr);
      setKsefDateFrom(dateFromStr);
    } else {
      // Resetuj stan przy zamykaniu
      reset();
      setSourceType(null);
      setSelectedFile(null);
      setFilePreviewUrl(null);
      setAdditionalAttachments([]);
      setKsefInvoices([]);
      setSelectedKsefInvoice(null);
      setSelectedKsefInvoices(new Set());
      setManualAmounts(false);
    }
  }, [isOpen, reset, user]);

  // Automatyczne pobieranie faktur z KSeF po wyborze źródła KSeF
  useEffect(() => {
    if (isOpen && sourceType === 'ksef' && ksefDateFrom && ksefDateTo) {
      handleSearchKsefInvoices(ksefDateFrom, ksefDateTo, ksefSubjectType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceType, isOpen, ksefDateFrom, ksefDateTo, ksefSubjectType]);

  // Automatyczne obliczanie kwoty VAT i brutto (tylko gdy tryb ręczny jest wyłączony)
  useEffect(() => {
    if (manualAmounts) return; // Nie przeliczaj gdy tryb ręczny jest włączony
    
    const net = parseFloat(amountNet) || 0;
    const rate = parseFloat(vatRate) || 0;
    
    if (net > 0 && rate >= 0) {
      const calculatedVat = (net * rate) / 100;
      const calculatedGross = net + calculatedVat;
      
      setValue('vatAmount', calculatedVat.toFixed(2), { shouldValidate: false });
      setValue('amountGross', calculatedGross.toFixed(2), { shouldValidate: false });
    } else if (net === 0 || rate === 0) {
      setValue('vatAmount', '0.00', { shouldValidate: false });
      if (net === 0) {
        setValue('amountGross', '0.00', { shouldValidate: false });
      } else {
        setValue('amountGross', net.toFixed(2), { shouldValidate: false });
      }
    }
  }, [amountNet, vatRate, setValue, manualAmounts]);

  // Obsługa zaznaczania tekstu z PDF (dla react-pdf i iframe)
  useEffect(() => {
    if (!scanningField) return;

    let selectionTimeout = null;

    const handleSelectionChange = () => {
      // Wyczyść poprzedni timeout
      if (selectionTimeout) {
        clearTimeout(selectionTimeout);
      }

      // Opóźnienie, aby upewnić się że zaznaczenie jest już gotowe
      selectionTimeout = setTimeout(() => {
        const selection = window.getSelection();
        const selectedText = selection && selection.toString().trim();
        
        console.log('🔍 [SKAN] Zaznaczenie zmienione:', {
          scanningField,
          selectedText,
          hasSelection: !!selectedText,
          rangeCount: selection?.rangeCount || 0,
          pdfError,
          numPages,
          hasContainer: !!pdfContainerRef.current
        });
        
        if (selectedText) {
          // Sprawdź czy zaznaczenie jest w kontenerze PDF (react-pdf)
          let isInPdfContainer = false;
          
          if (pdfContainerRef.current && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const container = range.commonAncestorContainer;
            if (container) {
              const checkNode = container.nodeType === Node.TEXT_NODE ? container.parentElement : container;
              if (checkNode && pdfContainerRef.current.contains(checkNode)) {
                isInPdfContainer = true;
              }
            }
            
            // Alternatywnie: sprawdź czy jakikolwiek element zaznaczenia jest w kontenerze
            if (!isInPdfContainer) {
              let node = range.startContainer;
              while (node && node !== document.body) {
                if (pdfContainerRef.current.contains(node)) {
                  isInPdfContainer = true;
                  break;
                }
                node = node.parentNode;
              }
            }
          }
          
          // Jeśli używamy iframe fallback, zaakceptuj każde zaznaczenie
          // (nie możemy sprawdzić czy jest w iframe z powodu cross-origin)
          const shouldAccept = isInPdfContainer || (pdfError || !numPages);
          
          console.log('🔍 [SKAN] Sprawdzanie akceptacji:', {
            isInPdfContainer,
            shouldAccept,
            pdfError,
            numPages
          });
          
          if (shouldAccept) {
            console.log('✅ [SKAN] Akceptuję zaznaczenie:', selectedText);
            
            // Wypełnij odpowiednie pole
            if (scanningField === 'supplierName') {
              setValue('supplierName', selectedText, { shouldValidate: false });
              toast.success(`Wybrano nazwę: ${selectedText}`);
            } else if (scanningField === 'supplierNip') {
              const normalized = selectedText.replace(/[\s\-_.]/g, '').toUpperCase();
              const isPolish = /^[0-9]{10}$/.test(normalized);
              const isForeign = /^[A-Z]{2}[A-Z0-9]{2,13}$/.test(normalized);
              if (isPolish || isForeign) {
                setValue('supplierNip', normalized, { shouldValidate: false });
                toast.success(`Wybrano NIP: ${normalized}`);
              } else {
                toast.error('NIP musi mieć 10 cyfr lub być zagranicznym numerem VAT (np. EU521412356)');
              }
            } else if (scanningField === 'number') {
              setValue('number', selectedText, { shouldValidate: false });
              toast.success(`Wybrano numer faktury: ${selectedText}`);
            }
            
            // Wyłącz tryb skanowania
            setScanningField(null);
            
            // Wyczyść zaznaczenie
            selection.removeAllRanges();
          } else {
            console.log('❌ [SKAN] Odrzucono zaznaczenie - nie jest w kontenerze PDF');
          }
        }
      }, 300); // Zwiększone opóźnienie do 300ms
    };

    // Nasłuchuj na zmiany zaznaczenia (działa również z iframe)
    document.addEventListener('selectionchange', handleSelectionChange);
    
    // Dodatkowo nasłuchuj na mouseup dla lepszej responsywności
    const handleMouseUp = () => {
      handleSelectionChange();
    };
    document.addEventListener('mouseup', handleMouseUp);
    
    console.log('🎯 [SKAN] Nasłuchiwanie aktywne dla pola:', scanningField);
    
    return () => {
      if (selectionTimeout) {
        clearTimeout(selectionTimeout);
      }
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('mouseup', handleMouseUp);
      console.log('🛑 [SKAN] Nasłuchiwanie wyłączone');
    };
  }, [scanningField, setValue, pdfError, numPages]);

  // Funkcja do włączenia trybu skanowania
  const handleStartScanning = (fieldName) => {
    if (!filePreviewUrl && !selectedFile) {
      toast.error('Najpierw wybierz plik PDF');
      return;
    }
    setScanningField(fieldName);
    const fieldLabel = fieldName === 'supplierName' ? 'Nazwa dostawcy' : fieldName === 'supplierNip' ? 'NIP dostawcy' : 'Numer faktury';
    toast(`Tryb skanowania aktywny dla "${fieldLabel}"`, { 
      duration: 3000,
      icon: '📄'
    });
  };

  // Obsługa załadowania PDF
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setPdfError(null);
  };

  const onPageLoadSuccess = (page) => {
    if (page && page.originalWidth && page.originalHeight) {
      setPageAspectRatio(page.originalHeight / page.originalWidth);
    } else if (page && page.width && page.height) {
      setPageAspectRatio(page.height / page.width);
    }
  };

  // Obserwacja rozmiaru kontenera podglądu, aby PDF dopasowywał się bez przewijania
  useEffect(() => {
    const el = pdfContainerRef.current;
    if (!el) return;
    const update = () => {
      setContainerSize({ width: el.clientWidth, height: el.clientHeight });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [filePreviewUrl]);

  // Reset zoomu przy zmianie pliku
  useEffect(() => {
    setPdfZoom(1);
  }, [filePreviewUrl]);

  // Zoom kółkiem myszy nad podglądem PDF
  useEffect(() => {
    const el = pdfContainerRef.current;
    if (!el || !filePreviewUrl) return;

    const handleWheel = (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setPdfZoom(prev => {
        const next = Math.round((prev + delta) * 100) / 100;
        return Math.max(0.5, Math.min(4, next));
      });
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [filePreviewUrl]);

  const onDocumentLoadError = (error) => {
    console.error('Błąd podczas ładowania PDF:', error);
    setPdfError('Nie udało się załadować PDF');
    setUseIframeFallback(true); // Przełącz na iframe fallback
  };

  // Obsługa wklejania tekstu ze schowka do pola podczas skanowania (dla iframe fallback)
  useEffect(() => {
    if (!scanningField) return;

    const handlePaste = async (e) => {
      try {
        const pastedText = (e.clipboardData || window.clipboardData).getData('text');
        if (pastedText && pastedText.trim()) {
          const trimmedText = pastedText.trim();
          
          // Wypełnij odpowiednie pole
          if (scanningField === 'supplierName') {
            setValue('supplierName', trimmedText, { shouldValidate: false });
            toast.success(`Wklejono nazwę: ${trimmedText}`);
          } else if (scanningField === 'supplierNip') {
            const normalized = trimmedText.replace(/[\s\-_.]/g, '').toUpperCase();
            const isPolish = /^[0-9]{10}$/.test(normalized);
            const isForeign = /^[A-Z]{2}[A-Z0-9]{2,13}$/.test(normalized);
            if (isPolish || isForeign) {
              setValue('supplierNip', normalized, { shouldValidate: false });
              toast.success(`Wklejono NIP: ${normalized}`);
            } else {
              toast.error('NIP musi mieć 10 cyfr lub być zagranicznym numerem VAT (np. EU521412356)');
            }
          } else if (scanningField === 'number') {
            setValue('number', trimmedText, { shouldValidate: false });
            toast.success(`Wklejono numer faktury: ${trimmedText}`);
          }
          
          // Wyłącz tryb skanowania
          setScanningField(null);
        }
      } catch (error) {
        console.error('Błąd podczas wklejania:', error);
      }
    };

    // Nasłuchuj na zdarzenie paste w całym dokumencie
    document.addEventListener('paste', handlePaste);
    
    // Nasłuchuj również na kliknięcie w pole - jeśli użytkownik skopiował tekst z iframe,
    // automatycznie wkleimy go po kliknięciu w pole
    const handleFieldClick = async (e) => {
      if (!scanningField) return;
      
      // Sprawdź czy kliknięcie było w odpowiednim polu
      const fieldName = e.target.name || e.target.id;
      const isTargetField = 
        (scanningField === 'supplierName' && (fieldName === 'supplierName' || fieldName === 'supplier-name')) ||
        (scanningField === 'supplierNip' && (fieldName === 'supplierNip' || fieldName === 'supplier-nip')) ||
        (scanningField === 'number' && (fieldName === 'number' || fieldName === 'invoice-number'));
      
      if (isTargetField) {
        try {
          // Spróbuj odczytać tekst ze schowka
          const clipboardText = await navigator.clipboard.readText();
          if (clipboardText && clipboardText.trim()) {
            const trimmedText = clipboardText.trim();
            
            // Wypełnij odpowiednie pole
            if (scanningField === 'supplierName') {
              setValue('supplierName', trimmedText, { shouldValidate: false });
              toast.success(`Wklejono nazwę: ${trimmedText}`);
            } else if (scanningField === 'supplierNip') {
              const normalized = trimmedText.replace(/[\s\-_.]/g, '').toUpperCase();
              const isPolish = /^[0-9]{10}$/.test(normalized);
              const isForeign = /^[A-Z]{2}[A-Z0-9]{2,13}$/.test(normalized);
              if (isPolish || isForeign) {
                setValue('supplierNip', normalized, { shouldValidate: false });
                toast.success(`Wklejono NIP: ${normalized}`);
              } else {
                toast.error('NIP musi mieć 10 cyfr lub być zagranicznym numerem VAT (np. EU521412356)');
              }
            } else if (scanningField === 'number') {
              setValue('number', trimmedText, { shouldValidate: false });
              toast.success(`Wklejono numer faktury: ${trimmedText}`);
            }
            
            // Wyłącz tryb skanowania
            setScanningField(null);
          }
        } catch (error) {
          // Clipboard API może nie być dostępne - użyj standardowego paste
          console.warn('Nie można odczytać schowka:', error);
        }
      }
    };
    
    document.addEventListener('click', handleFieldClick);
    
    return () => {
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('click', handleFieldClick);
    };
  }, [scanningField, setValue]);


  // Pobierz faktury z KSeF
  const handleSearchKsefInvoices = async (dateFrom, dateTo, subjectType) => {
    if (!dateFrom || !dateTo) {
      toast.error('Wybierz zakres dat');
      return;
    }
    
    setSearchingKsef(true);
    try {
      const response = await api.post('/ksef/invoices/metadata', {
        dateFrom,
        dateTo,
        pageOffset: 0,
        pageSize: 100,
        sortOrder: 'Desc',
        subjectType: subjectType || 'Subject2'
      });
      
      if (response.data.success) {
        const invoices = response.data.invoices || [];
        setKsefInvoices(invoices);
        
        // Sprawdź które faktury są już zaimportowane
        if (invoices.length > 0) {
          const ksefReferences = invoices.map(inv => inv.ksefNumber).filter(ref => ref);
          if (ksefReferences.length > 0) {
            try {
              const checkResponse = await api.post('/ksef/invoices/check-imported', {
                ksefReferences: ksefReferences
              });
              if (checkResponse.data.imported) {
                setImportedKsefReferences(new Set(checkResponse.data.imported));
              }
            } catch (error) {
              console.error('Błąd podczas sprawdzania zaimportowanych faktur:', error);
            }
          }
        }
        
        if (invoices.length > 0) {
          toast.success(`Znaleziono ${invoices.length} faktur z KSeF`);
        } else {
          toast.info('Nie znaleziono faktur w wybranym zakresie dat');
        }
      }
    } catch (error) {
      console.error('Błąd podczas pobierania faktur z KSeF:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Błąd podczas pobierania faktur z KSeF';
      toast.error(errorMessage);
      setKsefInvoices([]);
    } finally {
      setSearchingKsef(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } catch (e) {
      return dateStr;
    }
  };

  const handleSelectKsefInvoice = (invoice) => {
    if (importedKsefReferences.has(invoice.ksefNumber)) {
      toast.error('Ta faktura została już zaimportowana do systemu');
      return;
    }
    
    setSelectedKsefInvoice(invoice);
    setValue('ksefReference', invoice.ksefNumber, { shouldValidate: false });
    if (invoice.sellerNip) {
      setValue('supplierNip', invoice.sellerNip, { shouldValidate: false });
    }
    toast.success(`Wybrano fakturę: ${invoice.invoiceNumber || invoice.ksefNumber}`);
  };

  const handleToggleKsefInvoiceCheckbox = (invoice, checked) => {
    if (importedKsefReferences.has(invoice.ksefNumber)) return;
    setSelectedKsefInvoices(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(invoice.ksefNumber);
      } else {
        next.delete(invoice.ksefNumber);
      }
      return next;
    });
  };

  const handleToggleAllKsefInvoices = (checked) => {
    if (checked) {
      const selectable = ksefInvoices.filter(inv => !importedKsefReferences.has(inv.ksefNumber));
      setSelectedKsefInvoices(new Set(selectable.map(inv => inv.ksefNumber)));
    } else {
      setSelectedKsefInvoices(new Set());
    }
  };

  const handlePreviewKsefInvoice = async (invoice) => {
    try {
      const response = await api.get(`/ksef/invoices/${invoice.ksefNumber}/preview`, {
        responseType: 'text'
      });
      
      const newWindow = window.open('', '_blank', 'width=1200,height=800');
      if (newWindow) {
        newWindow.document.write(response.data);
        newWindow.document.close();
      }
    } catch (error) {
      console.error('Błąd podczas pobierania podglądu faktury z KSeF:', error);
      toast.error('Nie udało się pobrać podglądu faktury z KSeF');
    }
  };

  // Funkcja do przetwarzania pliku (używana zarówno przez onChange jak i drag&drop)
  const processFile = async (file) => {
    if (!file) return;
    
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isXml = file.type === 'application/xml' || file.type === 'text/xml' || file.name.toLowerCase().endsWith('.xml');
    
    if (!isPdf && !isXml) {
      toast.error('Tylko pliki PDF lub XML są dozwolone');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Plik jest zbyt duży. Maksymalny rozmiar to 10MB');
      return;
    }
    
    setSelectedFile(file);
    
    // Utwórz URL podglądu dla PDF (zwolnij poprzedni jeśli istnieje)
    if (isPdf) {
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl);
      }
      const url = URL.createObjectURL(file);
      setFilePreviewUrl(url);
    } else {
      // Dla XML nie ma podglądu
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl);
      }
      setFilePreviewUrl(null);
    }
    
    // Automatyczne parsowanie PDF lub XML
    setParsing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const endpoint = isXml ? '/invoices/parse/xml' : '/invoices/parse/pdf';
      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const parsedData = response.data;
      
      // Wypełnianie formularza danymi z faktury
      if (parsedData.number !== undefined && parsedData.number !== null) {
        setValue('number', String(parsedData.number).trim(), { shouldValidate: false });
      }
      if (parsedData.supplierName !== undefined && parsedData.supplierName !== null) {
        setValue('supplierName', String(parsedData.supplierName).trim(), { shouldValidate: false });
      }
      if (parsedData.supplierNip !== undefined && parsedData.supplierNip !== null) {
        setValue('supplierNip', String(parsedData.supplierNip).trim(), { shouldValidate: false });
      }
      if (parsedData.issueDate !== undefined && parsedData.issueDate !== null) {
        let dateStr = String(parsedData.issueDate).trim();
        if (dateStr.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
          const [day, month, year] = dateStr.split('.');
          dateStr = `${year}-${month}-${day}`;
        }
        setValue('issueDate', dateStr, { shouldValidate: false });
      }
      if (parsedData.dueDate !== undefined && parsedData.dueDate !== null) {
        let dateStr = String(parsedData.dueDate).trim();
        if (dateStr.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
          const [day, month, year] = dateStr.split('.');
          dateStr = `${year}-${month}-${day}`;
        }
        setValue('dueDate', dateStr, { shouldValidate: false });
      }
      if (parsedData.amountNet !== undefined && parsedData.amountNet !== null) {
        const amount = parseFloat(parsedData.amountNet);
        if (!isNaN(amount)) {
          setValue('amountNet', amount.toFixed(2), { shouldValidate: false });
        }
      }
      if (parsedData.vatRate !== undefined && parsedData.vatRate !== null) {
        const rate = parseFloat(parsedData.vatRate);
        if (!isNaN(rate) && rate >= 0) {
          setValue('vatRate', rate.toFixed(2), { shouldValidate: false });
        }
      }
      if (parsedData.vatAmount !== undefined && parsedData.vatAmount !== null) {
        const amount = parseFloat(parsedData.vatAmount);
        if (!isNaN(amount) && amount >= 0) {
          setValue('vatAmount', amount.toFixed(2), { shouldValidate: false });
        }
      }
      if (parsedData.amountGross !== undefined && parsedData.amountGross !== null) {
        const amount = parseFloat(parsedData.amountGross);
        if (!isNaN(amount)) {
          setValue('amountGross', amount.toFixed(2), { shouldValidate: false });
        }
      }
      if (parsedData.currency !== undefined && parsedData.currency !== null) {
        setValue('currency', String(parsedData.currency).trim().toUpperCase(), { shouldValidate: false });
      }
      
      // Jeśli plik zawiera kwoty, włącz tryb ręczny aby zachować wartości z pliku
      if ((parsedData.amountNet !== undefined && parsedData.amountNet !== null) ||
          (parsedData.vatAmount !== undefined && parsedData.vatAmount !== null) ||
          (parsedData.amountGross !== undefined && parsedData.amountGross !== null)) {
        setManualAmounts(true);
      }
      
      const filledFields = Object.keys(parsedData).filter(key => 
        parsedData[key] !== undefined && parsedData[key] !== null && String(parsedData[key]).trim() !== ''
      ).length;
      
      if (filledFields > 0) {
        toast.success(`Dane z faktury ${isXml ? 'XML' : 'PDF'} zostały automatycznie wczytane (${filledFields} pól)`);
      } else {
        toast(`Nie udało się odczytać danych z faktury ${isXml ? 'XML' : 'PDF'}. Wypełnij formularz ręcznie.`, {
          icon: '⚠️',
          duration: 5000
        });
      }
    } catch (error) {
      console.error(`Błąd podczas parsowania ${isXml ? 'XML' : 'PDF'}:`, error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Nieznany błąd';
      toast.error(`Nie udało się automatycznie odczytać danych z faktury: ${errorMessage}. Możesz wypełnić formularz ręcznie.`);
    } finally {
      setParsing(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await processFile(file);
      e.target.value = ''; // Resetuj input aby można było wybrać ten sam plik ponownie
    }
  };

  // Handlery dla drag and drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      await processFile(file);
    }
  };

  const validateAttachmentFile = (file) => {
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    const allowedExtensions = ['.pdf', '.png', '.jpg', '.jpeg'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    return (allowedTypes.includes(file.type) || allowedExtensions.includes(ext)) && file.size <= 10 * 1024 * 1024;
  };

  const handleAttachmentsDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAttachments(true);
  };
  const handleAttachmentsDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAttachments(false);
  };
  const handleAttachmentsDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAttachments(false);
    const files = Array.from(e.dataTransfer.files || []);
    const validFiles = files.filter(validateAttachmentFile);
    if (validFiles.length > 0) {
      setAdditionalAttachments(prev => [...prev, ...validFiles]);
    }
    if (files.length > 0 && validFiles.length < files.length) {
      toast.error('Niektóre pliki odrzucone. Dozwolone: PDF, PNG, JPG do 10MB.');
    }
  };

  const onSubmitKsef = async (data) => {
    // Użyj zalogowanego użytkownika jeśli selectedEmployee nie jest ustawione
    const employeeId = selectedEmployee || (user?.id?.toString());
    
    if (!employeeId) {
      toast.error('Brak informacji o pracowniku');
      return;
    }
    
    const invoicesToImport = selectedKsefInvoices.size > 0
      ? ksefInvoices.filter(inv => selectedKsefInvoices.has(inv.ksefNumber))
      : (selectedKsefInvoice ? [selectedKsefInvoice] : []);

    if (invoicesToImport.length === 0) {
      toast.error('Wybierz fakturę z listy lub zaznacz kilka faktur do importu');
      return;
    }

    setLoading(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      const throttleMs = 3000; // 1 request / 3 sekundy - ochrona limitów KSeF
      for (let i = 0; i < invoicesToImport.length; i++) {
        const invoice = invoicesToImport[i];
        if (i > 0) {
          await new Promise(r => setTimeout(r, throttleMs));
        }
        try {
          const response = await api.post('/invoices/import/ksef', {
            ksefReference: invoice.ksefNumber,
            supplierNip: invoice.sellerNip || data.supplierNip,
            metadata: invoice
          });
          const invoiceId = response.data?.id;
          if (additionalAttachments.length > 0 && invoiceId) {
            for (const file of additionalAttachments) {
              const attachmentFormData = new FormData();
              attachmentFormData.append('file', file);
              await api.post(`/invoices/${invoiceId}/attachments`, attachmentFormData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
            }
          }
          successCount++;
          setImportedKsefReferences(prev => new Set([...prev, invoice.ksefNumber]));
          setSelectedKsefInvoices(prev => {
            const next = new Set(prev);
            next.delete(invoice.ksefNumber);
            return next;
          });
        } catch (err) {
          errorCount++;
          const msg = err.response?.data?.error || err.response?.data?.message || err.message;
          toast.error(`Błąd importu ${invoice.invoiceNumber || invoice.ksefNumber}: ${msg}`);
        }
      }
      if (successCount > 0) {
        toast.success(successCount === 1
          ? (additionalAttachments.length > 0 ? 'Faktura została zaimportowana z KSeF wraz z załącznikami' : 'Faktura została zaimportowana z KSeF')
          : `Zaimportowano ${successCount} faktur z KSeF`);
        if (onSuccess) onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Błąd podczas importu faktury z KSeF:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Błąd podczas importu faktury z KSeF';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const onSubmitFile = async (data) => {
    // Użyj zalogowanego użytkownika jeśli selectedEmployee nie jest ustawione
    const employeeId = selectedEmployee || (user?.id?.toString());
    
    if (!employeeId) {
      toast.error('Brak informacji o pracowniku');
      return;
    }

    if (!selectedFile) {
      toast.error('Proszę wybrać plik PDF lub XML');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (data.number) formData.append('number', data.number);
      if (data.supplierName) formData.append('supplierName', data.supplierName);
      if (data.supplierNip) formData.append('supplierNip', data.supplierNip);
      if (data.issueDate) formData.append('issueDate', data.issueDate);
      if (data.dueDate) formData.append('dueDate', data.dueDate);
      if (data.amountNet) formData.append('amountNet', data.amountNet);
      if (data.vatRate) formData.append('vatRate', data.vatRate);
      if (data.vatAmount) formData.append('vatAmount', data.vatAmount);
      if (data.amountGross) formData.append('amountGross', data.amountGross);
      if (data.currency) formData.append('currency', data.currency);

      const isXml = selectedFile.type === 'application/xml' || selectedFile.type === 'text/xml' || selectedFile.name.toLowerCase().endsWith('.xml');
      const endpoint = isXml ? '/invoices/upload/xml' : '/invoices/upload/pdf';
      
      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const invoiceId = response.data?.id;
      
      // Jeśli są dodatkowe załączniki, dodaj je
      if (additionalAttachments.length > 0 && invoiceId) {
        setUploadingAttachments(true);
        try {
          for (const file of additionalAttachments) {
            const attachmentFormData = new FormData();
            attachmentFormData.append('file', file);
            await api.post(`/invoices/${invoiceId}/attachments`, attachmentFormData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });
          }
          toast.success(`Faktura została dodana z pliku ${isXml ? 'XML' : 'PDF'} wraz z załącznikami`);
        } catch (error) {
          console.error('Błąd podczas dodawania załączników:', error);
          toast.success(`Faktura została dodana z pliku ${isXml ? 'XML' : 'PDF'}, ale wystąpił błąd podczas dodawania załączników`);
        } finally {
          setUploadingAttachments(false);
        }
      } else {
        toast.success(`Faktura została dodana z pliku ${isXml ? 'XML' : 'PDF'}`);
      }
      
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Błąd podczas dodawania faktury';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Sprawdź uprawnienia - tylko SEKRETARIAT i SUPERADMIN mogą dodawać faktury
  if (user?.role !== 'SEKRETARIAT' && user?.role !== 'SUPERADMIN') {
    toast.error('Brak uprawnień do dodawania faktur. Tylko pracownicy sekretariatu mogą dodawać faktury.');
    onClose();
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Dodaj fakturę</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className={`flex-1 p-6 flex flex-col min-h-0 ${sourceType === 'file' ? 'overflow-hidden' : 'overflow-y-auto'}`}>
            {/* Wybór źródła */}
            {!sourceType && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Źródło faktury <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setSourceType('ksef')}
                  className="flex items-center justify-center gap-3 px-6 py-4 border-2 border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
                >
                  <Key className="h-6 w-6 text-primary-600" />
                  <span className="text-lg font-medium">Z KSeF</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSourceType('file');
                    setManualAmounts(false); // Resetuj tryb ręczny przy zmianie źródła
                  }}
                  className="flex items-center justify-center gap-3 px-6 py-4 border-2 border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
                >
                  <Upload className="h-6 w-6 text-primary-600" />
                  <span className="text-lg font-medium">Z pliku</span>
                </button>
              </div>
            </div>
          )}

          {/* KSeF */}
          {sourceType === 'ksef' && (
            <div className="space-y-5">
              {/* Wyszukiwanie faktur KSeF - zwarty pasek filtrów */}
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Data od</label>
                  <input
                    type="datetime-local"
                    value={ksefDateFrom}
                    onChange={(e) => setKsefDateFrom(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Data do</label>
                  <input
                    type="datetime-local"
                    value={ksefDateTo}
                    onChange={(e) => setKsefDateTo(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Typ faktur</label>
                  <select
                    value={ksefSubjectType}
                    onChange={(e) => setKsefSubjectType(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="Subject2">Faktury zakupowe</option>
                    <option value="Subject1">Faktury sprzedażowe</option>
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => handleSearchKsefInvoices(ksefDateFrom, ksefDateTo, ksefSubjectType)}
                  disabled={searchingKsef || !ksefDateFrom || !ksefDateTo}
                  className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {searchingKsef ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      Szukanie...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4" />
                      Wyszukaj
                    </>
                  )}
                </button>
              </div>

              {/* Lista faktur z KSeF */}
              {ksefInvoices.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                    <span className="text-sm font-semibold text-gray-800">
                      Znalezione faktury: {ksefInvoices.length}
                    </span>
                  </div>
                  <div className="max-h-72 overflow-y-auto overflow-x-hidden">
                    <table className="w-full divide-y divide-gray-200 table-fixed">
                      <colgroup>
                        <col style={{ width: '6%' }} />
                        <col style={{ width: '4%' }} />
                        <col style={{ width: '4%' }} />
                        <col style={{ width: '14%' }} />
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '8%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '6%' }} />
                        <col style={{ width: '6%' }} />
                        <col style={{ width: '4%' }} />
                      </colgroup>
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">Wybierz</th>
                          <th className="px-1 py-1.5 text-center text-[10px] font-medium text-gray-500 uppercase">Podgląd</th>
                          <th className="px-1 py-1.5 text-center text-[10px] font-medium text-gray-500 uppercase">
                            <input
                              type="checkbox"
                              checked={ksefInvoices.filter(inv => !importedKsefReferences.has(inv.ksefNumber)).length > 0 &&
                                selectedKsefInvoices.size === ksefInvoices.filter(inv => !importedKsefReferences.has(inv.ksefNumber)).length}
                              onChange={(e) => handleToggleAllKsefInvoices(e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              title="Zaznacz wszystkie"
                            />
                          </th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">Nr KSeF</th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">Nr faktury</th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">Sprzedawca</th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">NIP</th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">Data wyst.</th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">Data KSeF</th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">Data otrz.</th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">Netto</th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">Brutto</th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">VAT</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {ksefInvoices.map((invoice, index) => {
                          const isImported = importedKsefReferences.has(invoice.ksefNumber);
                          const isSelected = selectedKsefInvoice?.ksefNumber === invoice.ksefNumber;
                          const isChecked = selectedKsefInvoices.has(invoice.ksefNumber);
                          return (
                            <tr 
                              key={index} 
                              className={`hover:bg-gray-50 ${isImported ? 'bg-green-50' : ''} ${isSelected ? 'bg-blue-100' : ''} ${!isImported ? 'cursor-pointer' : ''}`}
                              onClick={() => !isImported && handleSelectKsefInvoice(invoice)}
                            >
                              <td className="px-1 py-1.5">
                                {!isImported ? (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSelectKsefInvoice(invoice);
                                    }}
                                    className={`px-2 py-0.5 text-[10px] rounded ${isSelected ? 'bg-primary-700 text-white' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
                                  >
                                    {isSelected ? 'Wybrano' : 'Wybierz'}
                                  </button>
                                ) : (
                                  <span className="px-1.5 py-0.5 text-[10px] text-green-700 bg-green-100 rounded font-semibold" title="Zaimportowana">
                                    Imp.
                                  </span>
                                )}
                              </td>
                              <td className="px-1 py-1.5 text-center">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePreviewKsefInvoice(invoice);
                                  }}
                                  className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                                  title="Podgląd faktury z KSeF"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </button>
                              </td>
                              <td className="px-1 py-1.5 text-center">
                                {!isImported ? (
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleToggleKsefInvoiceCheckbox(invoice, e.target.checked);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    title="Zaznacz do importu"
                                  />
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                              <td className="px-1 py-1.5 text-xs font-mono min-w-0 overflow-hidden" title={invoice.ksefNumber || ''}>
                                <span className="inline-flex items-center gap-0.5 min-w-0 max-w-full">
                                  <span className="truncate">{invoice.ksefNumber || '-'}</span>
                                  {invoice.hasAttachment && (
                                    <span className="flex-shrink-0 text-green-600" title="Faktura ma załącznik w KSeF">
                                      <Paperclip className="h-3 w-3" />
                                    </span>
                                  )}
                                </span>
                              </td>
                              <td className="px-1 py-1.5 text-xs font-mono min-w-0 overflow-hidden" title={invoice.invoiceNumber || ''}>
                                <span className="block truncate">{invoice.invoiceNumber || '-'}</span>
                              </td>
                              <td className="px-1 py-1.5 text-xs min-w-0 overflow-hidden" title={invoice.sellerName || ''}>
                                <span className="block truncate">{invoice.sellerName || '-'}</span>
                              </td>
                              <td className="px-1 py-1.5 text-xs tabular-nums">{invoice.sellerNip || '-'}</td>
                              <td className="px-1 py-1.5 text-xs tabular-nums">{formatDate(invoice.issueDate)}</td>
                              <td className="px-1 py-1.5 text-xs tabular-nums">{formatDate(invoice.permanentStorageDate)}</td>
                              <td className="px-1 py-1.5 text-xs tabular-nums">{formatDate(invoice.invoicingDate)}</td>
                              <td className="px-1 py-1.5 text-xs tabular-nums">{invoice.netAmount != null ? `${invoice.netAmount.toFixed(2)}` : '-'}</td>
                              <td className="px-1 py-1.5 text-xs tabular-nums">{invoice.grossAmount != null ? `${invoice.grossAmount.toFixed(2)}` : '-'}</td>
                              <td className="px-1 py-1.5 text-xs tabular-nums">{invoice.vatAmount != null ? `${invoice.vatAmount.toFixed(2)}` : '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Formularz importu z KSeF */}
              <form onSubmit={handleSubmit(onSubmitKsef)} className="space-y-5 pt-5 mt-5 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="ksefReference" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Referencja KSeF <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register('ksefReference')}
                      type="text"
                      id="ksefReference"
                      className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      placeholder="Wybierz fakturę z listy lub wpisz referencję"
                      readOnly={!!selectedKsefInvoice}
                    />
                    {errors.ksefReference && (
                      <p className="mt-1 text-xs text-red-600">{errors.ksefReference.message}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="supplierNip" className="block text-sm font-medium text-gray-700 mb-1.5">
                      NIP dostawcy
                    </label>
                    <input
                      {...register('supplierNip', {
                        setValueAs: (v) => (v ? v.toString().replace(/\s+/g, '').toUpperCase() : v),
                        pattern: {
                          value: /^$|^[0-9]{10}$|^[A-Za-z]{2}[A-Za-z0-9]{2,13}$/,
                          message: 'NIP musi mieć 10 cyfr lub być zagranicznym numerem VAT (np. EU521412356)'
                        }
                      })}
                      type="text"
                      id="supplierNip"
                      className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 uppercase"
                    placeholder="1234567890 lub EU521412356"
                    maxLength="15"
                  />
                    {errors.supplierNip && (
                      <p className="mt-1 text-xs text-red-600">{errors.supplierNip.message}</p>
                    )}
                  </div>
                </div>

                {/* Sekcja załączników */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Dodatkowe załączniki (PDF, PNG, JPG) - opcjonalnie
                  </label>
                  <div className="space-y-3">
                    <div
                      className={`flex justify-center items-center px-4 py-4 border-2 border-dashed rounded-lg transition-colors cursor-pointer min-h-[80px] ${
                        isDraggingAttachments ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-gray-400 bg-gray-50/50'
                      }`}
                      onDragOver={handleAttachmentsDragOver}
                      onDragLeave={handleAttachmentsDragLeave}
                      onDrop={handleAttachmentsDrop}
                      onClick={() => document.getElementById('additional-attachments-ksef')?.click()}
                    >
                      <input
                        type="file"
                        id="additional-attachments-ksef"
                        accept=".pdf,.png,.jpg,.jpeg"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          const validFiles = files.filter(validateAttachmentFile);
                          setAdditionalAttachments(prev => [...prev, ...validFiles]);
                          e.target.value = '';
                        }}
                        className="hidden"
                      />
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Upload className="h-4 w-4" />
                        <span>Przeciągnij pliki tutaj lub kliknij aby wybrać</span>
                      </div>
                    </div>
                    
                    {additionalAttachments.length > 0 && (
                      <div className="space-y-2">
                        {additionalAttachments.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
                              <span className="text-sm text-gray-700 truncate" title={file.name}>
                                {file.name}
                              </span>
                              <span className="text-xs text-gray-500">
                                ({(file.size / 1024).toFixed(1)} KB)
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setAdditionalAttachments(prev => prev.filter((_, i) => i !== index));
                              }}
                              className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                              title="Usuń"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Anuluj
                  </button>
                  <button
                    type="submit"
                    disabled={loading || uploadingAttachments}
                    className="px-4 py-2 text-sm text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {loading || uploadingAttachments ? (
                      <span className="flex items-center">
                        <LoadingSpinner size="sm" />
                        <span className="ml-2">{uploadingAttachments ? 'Dodawanie załączników...' : 'Importowanie...'}</span>
                      </span>
                    ) : (
                      'Importuj z KSeF'
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Z pliku */}
          {sourceType === 'file' && (
            <form onSubmit={handleSubmit(onSubmitFile)} className="flex flex-col flex-1 min-h-0 h-full">
              <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4 flex-shrink-0">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Upload className="h-5 w-5 text-green-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm text-green-800">Upload pliku PDF/XML</h3>
                    <p className="mt-1 text-sm text-green-700">
                      Prześlij plik PDF, XML (KSeF) lub skan faktury. Możesz opcjonalnie uzupełnić dane faktury ręcznie.
                    </p>
                  </div>
                </div>
              </div>

              {/* Layout dwukolumnowy */}
              <div className="grid grid-cols-2 gap-6 flex-1 min-h-0">
                {/* Lewa kolumna - Formularz (scrollowana) */}
                <div className="space-y-4 overflow-y-auto pr-2 min-h-0">
                  <div>
                    <label htmlFor="file" className="block text-sm text-gray-700 mb-2">
                      Plik PDF/XML <span className="text-red-500">*</span>
                    </label>
                    <div 
                      className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
                        isDragging 
                          ? 'border-primary-500 bg-primary-50' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      onDragEnter={handleDragEnter}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <div className="space-y-1 text-center">
                        <FileText className="mx-auto h-12 w-12 text-gray-400" />
                        {parsing ? (
                          <div className="flex flex-col items-center">
                            <LoadingSpinner size="sm" />
                            <p className="mt-2 text-sm text-gray-600">Odczytywanie danych z faktury...</p>
                          </div>
                        ) : (
                          <>
                            <div className="flex text-sm text-gray-600">
                              <label htmlFor="file" className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500">
                                <span>Wybierz plik</span>
                                <input
                                  id="file"
                                  name="file"
                                  type="file"
                                  accept=".pdf,application/pdf,.xml,application/xml,text/xml"
                                  onChange={handleFileChange}
                                  className="sr-only"
                                  disabled={parsing}
                                />
                              </label>
                              <p className="pl-1">lub przeciągnij i upuść</p>
                            </div>
                            <p className="text-xs text-gray-500">PDF lub XML do 10MB</p>
                            {selectedFile && (
                              <p className="text-sm text-green-600 mt-2">
                                Wybrany plik: {selectedFile.name}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label htmlFor="number" className="block text-sm text-gray-700 mb-2 flex items-center justify-between">
                        <span>Numer faktury</span>
                        {filePreviewUrl && (
                          <button
                            type="button"
                            onClick={() => handleStartScanning('number')}
                            className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                              scanningField === 'number' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            title="Zaznacz numer faktury z podglądu PDF"
                          >
                            <ScanLine className="h-3 w-3" />
                            SKAN
                          </button>
                        )}
                      </label>
                      <input
                        {...register('number')}
                        type="text"
                        id="number"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder="FV/2024/001"
                      />
                    </div>

                    <div className="relative" ref={contractorSearchRef}>
                      <label htmlFor="supplierName" className="block text-sm text-gray-700 mb-2 flex items-center justify-between">
                        <span>Nazwa dostawcy</span>
                        {filePreviewUrl && (
                          <button
                            type="button"
                            onClick={() => handleStartScanning('supplierName')}
                            className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                              scanningField === 'supplierName' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            title="Zaznacz nazwę dostawcy z podglądu PDF"
                          >
                            <ScanLine className="h-3 w-3" />
                            SKAN
                          </button>
                        )}
                      </label>
                      <div className="relative">
                        <input
                          {...register('supplierName')}
                          type="text"
                          id="supplierName"
                          className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Wpisz lub wyszukaj firmę z bazy"
                          autoComplete="off"
                          onFocus={() => contractorResults.length > 0 && setShowContractorDropdown(true)}
                        />
                        {contractorSearchLoading && (
                          <div className="absolute right-2 top-1/2 -translate-y-1/2">
                            <Loader className="h-4 w-4 animate-spin text-gray-400" />
                          </div>
                        )}
                      </div>
                      {showContractorDropdown && contractorResults.length > 0 && (
                        <ul className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                          {contractorResults.map((c) => (
                            <li key={c.id}>
                              <button
                                type="button"
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 flex flex-col"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setValue('supplierName', c.name, { shouldValidate: false });
                                  const nipVal = (c.nip || '').startsWith('EXT') ? '' : (c.nip || '');
                                  setValue('supplierNip', nipVal, { shouldValidate: false });
                                  setShowContractorDropdown(false);
                                  setContractorResults([]);
                                }}
                              >
                                <span className="font-medium text-gray-900">{c.name}</span>
                                {c.nip && !c.nip.startsWith('EXT') && (
                                  <span className="text-xs text-gray-500">NIP: {c.nip}</span>
                                )}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div>
                      <label htmlFor="supplierNip" className="block text-sm text-gray-700 mb-2 flex items-center justify-between">
                        <span>NIP dostawcy</span>
                        {filePreviewUrl && (
                          <button
                            type="button"
                            onClick={() => handleStartScanning('supplierNip')}
                            className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                              scanningField === 'supplierNip' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            title="Zaznacz NIP dostawcy z podglądu PDF"
                          >
                            <ScanLine className="h-3 w-3" />
                            SKAN
                          </button>
                        )}
                      </label>
                      <input
                        {...register('supplierNip', {
                          setValueAs: (v) => (v ? v.toString().replace(/\s+/g, '').toUpperCase() : v),
                          pattern: {
                            value: /^$|^[0-9]{10}$|^[A-Za-z]{2}[A-Za-z0-9]{2,13}$/,
                            message: 'NIP musi mieć 10 cyfr lub być zagranicznym numerem VAT (np. EU521412356)'
                          }
                        })}
                        type="text"
                        id="supplierNip"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 uppercase"
                        placeholder="1234567890 lub EU521412356"
                        maxLength="15"
                      />
                      {errors.supplierNip && (
                        <p className="mt-1 text-sm text-red-600">{errors.supplierNip.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="currency" className="block text-sm text-gray-700 mb-2">
                        Waluta
                      </label>
                      <select
                        {...register('currency')}
                        id="currency"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="PLN">PLN</option>
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                        <option value="UAH">UAH (грн)</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="issueDate" className="block text-sm text-gray-700 mb-2">
                        Data wystawienia
                      </label>
                      <input
                        {...register('issueDate')}
                        type="date"
                        id="issueDate"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="dueDate" className="block text-sm text-gray-700 mb-2">
                        Termin płatności
                      </label>
                      <input
                        {...register('dueDate')}
                        type="date"
                        id="dueDate"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    {/* Sekcja kwot z opcją ręcznego wprowadzania */}
                    <div className="space-y-4">
                      {/* Opcja ręcznego wprowadzania kwot */}
                      <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 shadow-sm">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="manualAmounts"
                            checked={manualAmounts}
                            onChange={(e) => {
                              setManualAmounts(e.target.checked);
                              if (!e.target.checked) {
                                // Gdy wyłączamy tryb ręczny, przelicz automatycznie
                                const net = parseFloat(amountNet) || 0;
                                const rate = parseFloat(vatRate) || 0;
                                if (net > 0 && rate >= 0) {
                                  const calculatedVat = (net * rate) / 100;
                                  const calculatedGross = net + calculatedVat;
                                  setValue('vatAmount', calculatedVat.toFixed(2), { shouldValidate: false });
                                  setValue('amountGross', calculatedGross.toFixed(2), { shouldValidate: false });
                                }
                              }
                            }}
                            className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer mt-0.5 flex-shrink-0"
                          />
                          <label htmlFor="manualAmounts" className="text-sm font-medium text-gray-700 cursor-pointer flex-1">
                            <span className="font-semibold text-blue-800 block mb-1">Wprowadź kwoty ręcznie</span>
                            <span className="block text-xs text-gray-600">
                              {manualAmounts 
                                ? "✓ Tryb ręczny włączony - możesz wprowadzić kwoty netto, VAT i brutto bez automatycznego przeliczania"
                                : "Kwoty VAT i brutto są obliczane automatycznie na podstawie kwoty netto i stawki VAT"}
                            </span>
                          </label>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="amountNet" className="block text-sm text-gray-700 mb-2">
                          Kwota netto
                        </label>
                        <input
                          {...register('amountNet', {})}
                          type="number"
                          step="0.01"
                          id="amountNet"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="0.00"
                        />
                        {errors.amountNet && (
                          <p className="mt-1 text-sm text-red-600">{errors.amountNet.message}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="vatRate" className="block text-sm text-gray-700 mb-2">
                          Stawka VAT (%)
                        </label>
                        <input
                          {...register('vatRate', {
                            min: {
                              value: 0,
                              message: 'Stawka VAT nie może być ujemna'
                            },
                            max: {
                              value: 100,
                              message: 'Stawka VAT nie może przekraczać 100%'
                            }
                          })}
                          type="number"
                          step="0.01"
                          id="vatRate"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="23.00"
                          disabled={manualAmounts}
                          title={manualAmounts ? "W trybie ręcznym stawka VAT nie jest używana do przeliczania" : ""}
                        />
                        {errors.vatRate && (
                          <p className="mt-1 text-sm text-red-600">{errors.vatRate.message}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="vatAmount" className="block text-sm text-gray-700 mb-2">
                          Kwota VAT
                        </label>
                        <input
                          {...register('vatAmount', {
                            min: {
                              value: 0,
                              message: 'Kwota VAT nie może być ujemna'
                            }
                          })}
                          type="number"
                          step="0.01"
                          id="vatAmount"
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${manualAmounts ? 'bg-white' : 'bg-gray-50'}`}
                          placeholder="0.00"
                          readOnly={!manualAmounts}
                          title={manualAmounts ? "Wprowadź kwotę VAT ręcznie" : "Obliczane automatycznie na podstawie kwoty netto i stawki VAT"}
                        />
                        {errors.vatAmount && (
                          <p className="mt-1 text-sm text-red-600">{errors.vatAmount.message}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="amountGross" className="block text-sm text-gray-700 mb-2">
                          Kwota brutto
                        </label>
                        <input
                          {...register('amountGross', {})}
                          type="number"
                          step="0.01"
                          id="amountGross"
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${manualAmounts ? 'bg-white' : 'bg-gray-50'}`}
                          placeholder="0.00"
                          readOnly={!manualAmounts}
                          title={manualAmounts ? "Wprowadź kwotę brutto ręcznie" : "Obliczane automatycznie na podstawie kwoty netto i kwoty VAT"}
                        />
                        {errors.amountGross && (
                          <p className="mt-1 text-sm text-red-600">{errors.amountGross.message}</p>
                        )}
                      </div>
                    </div>

                    {/* Sekcja załączników */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Dodatkowe załączniki (PDF, PNG, JPG) - opcjonalnie
                      </label>
                      <div className="space-y-3">
                        <div
                          className={`flex justify-center items-center px-4 py-4 border-2 border-dashed rounded-lg transition-colors cursor-pointer min-h-[80px] ${
                            isDraggingAttachments ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-gray-400 bg-gray-50/50'
                          }`}
                          onDragOver={handleAttachmentsDragOver}
                          onDragLeave={handleAttachmentsDragLeave}
                          onDrop={handleAttachmentsDrop}
                          onClick={() => document.getElementById('additional-attachments')?.click()}
                        >
                          <input
                            type="file"
                            id="additional-attachments"
                            accept=".pdf,.png,.jpg,.jpeg"
                            multiple
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              const validFiles = files.filter(validateAttachmentFile);
                              setAdditionalAttachments(prev => [...prev, ...validFiles]);
                              e.target.value = '';
                            }}
                            className="hidden"
                          />
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Upload className="h-4 w-4" />
                            <span>Przeciągnij pliki tutaj lub kliknij aby wybrać</span>
                          </div>
                        </div>
                        
                        {additionalAttachments.length > 0 && (
                          <div className="space-y-2">
                            {additionalAttachments.map((file, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                  <span className="text-sm text-gray-700 truncate" title={file.name}>
                                    {file.name}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    ({(file.size / 1024).toFixed(1)} KB)
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setAdditionalAttachments(prev => prev.filter((_, i) => i !== index));
                                  }}
                                  className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                  title="Usuń"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Prawa kolumna - Podgląd */}
                <div className="border-l border-gray-200 pl-6 flex flex-col min-h-0 h-full">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2 flex-shrink-0">
                    <Eye className="h-4 w-4 text-gray-600" />
                    Podgląd faktury
                  </h4>
                  <div className="bg-gray-50 rounded-lg border border-gray-200 relative flex-1 flex flex-col min-h-0 overflow-hidden">
                    {scanningField && (
                      <div className="absolute top-2 left-2 right-2 z-10 bg-blue-600 text-white px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2 shadow-lg">
                        <ScanLine className="h-4 w-4" />
                        <div className="flex-1">
                          <div className="font-semibold">Tryb skanowania aktywny</div>
                          <div className="text-xs opacity-90">
                            Zaznacz tekst w PDF dla: <strong>{scanningField === 'supplierName' ? 'Nazwa dostawcy' : scanningField === 'supplierNip' ? 'NIP dostawcy' : 'Numer faktury'}</strong>
                          </div>
                          <div className="text-xs opacity-75 mt-1">
                            Zaznacz tekst w PDF myszką - automatycznie wypełni się w polu
                          </div>
                        </div>
                        <button
                          onClick={() => setScanningField(null)}
                          className="ml-2 hover:bg-blue-700 rounded px-1 flex-shrink-0"
                          title="Anuluj skanowanie"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                    {filePreviewUrl ? (
                      <div
                        ref={pdfContainerRef}
                        className={`flex-1 min-h-0 flex flex-col ${pdfZoom > 1 ? 'overflow-auto' : 'overflow-hidden'}`}
                        title="Przewiń kółkiem myszy, aby przybliżyć/oddalić"
                      >
                        <Document
                          file={filePreviewUrl}
                          onLoadSuccess={onDocumentLoadSuccess}
                          onLoadError={onDocumentLoadError}
                          loading={
                            <div className="flex items-center justify-center p-8">
                              <LoadingSpinner />
                            </div>
                          }
                          error={
                            <div className="relative flex-1 flex flex-col">
                              <p className="text-red-600 mb-2 text-center">Błąd podczas ładowania PDF</p>
                              {pdfError && <p className="text-sm text-gray-600 mb-4 text-center">{pdfError}</p>}
                              <div className="relative flex-1">
                                <iframe
                                  ref={pdfIframeRef}
                                  src={filePreviewUrl}
                                  type="application/pdf"
                                  className="w-full h-full"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'block',
                                    border: 'none'
                                  }}
                                  title="Podgląd faktury"
                                />
                              </div>
                            </div>
                          }
                          className="flex-1 min-h-0 flex flex-col"
                        >
                          {numPages && (() => {
                            const NAV_HEIGHT = 36; // wysokość paska nawigacji stron + odstępy
                            const PADDING = 8;
                            const availableWidth = Math.max(0, containerSize.width - PADDING);
                            const availableHeight = Math.max(0, containerSize.height - NAV_HEIGHT - PADDING);
                            const widthFromHeight = availableHeight > 0 ? availableHeight / pageAspectRatio : availableWidth;
                            const baseWidth = Math.max(200, Math.min(availableWidth || 200, widthFromHeight || 200));
                            const computedWidth = baseWidth * pdfZoom;
                            return (
                              <>
                                <div className="flex items-center justify-center gap-2 mb-2 flex-shrink-0 sticky top-0 bg-gray-50 z-10 py-1">
                                  <button
                                    type="button"
                                    onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}
                                    disabled={pageNumber <= 1}
                                    className="px-2 py-1 text-sm bg-gray-200 rounded disabled:opacity-50"
                                  >
                                    ←
                                  </button>
                                  <span className="text-sm text-gray-600">
                                    Strona {pageNumber} z {numPages}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setPageNumber(prev => Math.min(numPages, prev + 1))}
                                    disabled={pageNumber >= numPages}
                                    className="px-2 py-1 text-sm bg-gray-200 rounded disabled:opacity-50"
                                  >
                                    →
                                  </button>
                                  <span className="mx-2 h-4 w-px bg-gray-300" />
                                  <button
                                    type="button"
                                    onClick={() => setPdfZoom(prev => Math.max(0.5, Math.round((prev - 0.1) * 100) / 100))}
                                    className="px-2 py-1 text-sm bg-gray-200 rounded"
                                    title="Pomniejsz"
                                  >
                                    −
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setPdfZoom(1)}
                                    className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded min-w-[44px]"
                                    title="Dopasuj do okna"
                                  >
                                    {Math.round(pdfZoom * 100)}%
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setPdfZoom(prev => Math.min(4, Math.round((prev + 0.1) * 100) / 100))}
                                    className="px-2 py-1 text-sm bg-gray-200 rounded"
                                    title="Powiększ"
                                  >
                                    +
                                  </button>
                                </div>
                                <div className="flex-1 min-h-0 flex items-start justify-center">
                                  <Page
                                    pageNumber={pageNumber}
                                    onLoadSuccess={onPageLoadSuccess}
                                    renderTextLayer={true}
                                    renderAnnotationLayer={true}
                                    className="border border-gray-200"
                                    width={computedWidth}
                                  />
                                </div>
                              </>
                            );
                          })()}
                        </Document>
                      </div>
                    ) : selectedFile ? (
                      <div className="flex items-center justify-center h-full p-8">
                        <div className="text-center">
                          <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500">Podgląd dostępny tylko dla plików PDF</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full p-8">
                        <div className="text-center">
                          <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500">Wybierz plik, aby zobaczyć podgląd</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4 mt-4 border-t flex-shrink-0">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={loading || uploadingAttachments || !selectedFile}
                  className="px-4 py-2 text-sm text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {(loading || uploadingAttachments) ? (
                    <span className="flex items-center">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">{uploadingAttachments ? 'Dodawanie załączników...' : 'Przesyłanie...'}</span>
                    </span>
                  ) : (
                    'Dodaj fakturę'
                  )}
                </button>
              </div>
            </form>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddInvoiceModal;
